import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Wrench, Plus, Search, Loader2, Database, Edit, Save, X, Trash2 } from 'lucide-react';
import { filterItemsBySearch } from '../utils/textSanitize';

const emptyForm = () => ({
  codigo: '',
  nivel1_area: '',
  nivel2_familia: '',
  nome: '',
  aplicacao: '',
  ordem: 0,
});

const TiposEquipamentosPage = () => {
  const { isRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'listTiposEquipamento');
      const r = await fn({ apenas_ativos: false });
      setItems(r.data?.data?.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar tipos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      filterItemsBySearch(items, search, ['nome', 'nivel1_area', 'nivel2_familia', 'codigo', 'aplicacao'], {
        matchDigits: true,
      }),
    [items, search]
  );

  const runSeed = async () => {
    if (!window.confirm('Carregar/atualizar tipos padrão da indústria alimentícia?')) return;
    setSeeding(true);
    setErr('');
    try {
      const fn = httpsCallable(functions, 'seedTiposEquipamento');
      const r = await fn({});
      const d = r.data?.data;
      setMsg(`Seed concluído: ${d?.criados || 0} novos, ${d?.atualizados || 0} atualizados.`);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro no seed');
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setModal(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      codigo: row.codigo || row.id,
      nivel1_area: row.nivel1_area || '',
      nivel2_familia: row.nivel2_familia || '',
      nome: row.nome || '',
      aplicacao: row.aplicacao || '',
      ordem: row.ordem || 0,
    });
    setModal(true);
  };

  const excluirTipo = async (row) => {
    const nome = row.nome || row.codigo || row.id;
    if (
      !window.confirm(
        `Excluir o tipo "${nome}"?\n\nEle deixará de aparecer em novos cadastros. Tipos já vinculados a equipamentos não podem ser excluídos.`
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    setErr('');
    try {
      await httpsCallable(functions, 'deleteTipoEquipamento')({ id: row.id });
      if (editId === row.id) setModal(false);
      setMsg(`Tipo "${nome}" excluído.`);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao excluir tipo');
    } finally {
      setDeletingId(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      if (editId) {
        const fn = httpsCallable(functions, 'updateTipoEquipamento');
        await fn({ id: editId, ...form, codigo: undefined });
        setMsg('Tipo atualizado.');
      } else {
        const fn = httpsCallable(functions, 'createTipoEquipamento');
        await fn(form);
        setMsg('Tipo criado.');
      }
      setModal(false);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!isRole('superadmin')) {
    return <div className="p-6 text-center text-gray-600">Acesso restrito ao superadmin.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap justify-between gap-3 items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="h-7 w-7 text-blue-600" />
            Tipos de Equipamentos
          </h1>
          <p className="text-gray-600 text-sm mt-1">Catálogo global consumido por fábricas e fornecedores de equipamentos.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runSeed}
            disabled={seeding}
            className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
            Carregar seed
          </button>
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg">
            <Plus className="h-4 w-4" /> Novo tipo
          </button>
        </div>
      </div>

      {msg && <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{msg}</div>}
      {err && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

      <div className="flex gap-2 items-stretch">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            className="w-full pl-9 border rounded-lg py-2 text-sm"
            placeholder="Buscar por nome, área, família ou código…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => setSearch('')}
          disabled={!search.trim()}
          className="shrink-0 px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          title="Limpar busca"
        >
          <X className="h-4 w-4" />
          Limpar busca
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Área / Família</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Aplicação</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((row) => (
                <tr key={row.id} className={row.ativo === false ? 'opacity-50' : ''}>
                  <td className="px-4 py-3 font-mono text-xs">{row.codigo || row.id}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div>{row.nivel1_area}</div>
                    <div className="text-xs">{row.nivel2_familia}</div>
                  </td>
                  <td className="px-4 py-3 font-medium">{row.nome}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{row.aplicacao}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {row.ativo !== false && (
                        <button
                          type="button"
                          onClick={() => excluirTipo(row)}
                          disabled={deletingId === row.id}
                          className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                          title="Excluir"
                        >
                          {deletingId === row.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold">{editId ? 'Editar tipo' : 'Novo tipo'}</h2>
            {!editId && (
              <div>
                <label className="text-xs text-gray-500">Código</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                />
              </div>
            )}
            {['nivel1_area', 'nivel2_familia', 'nome', 'aplicacao'].map((f) => (
              <div key={f}>
                <label className="text-xs text-gray-500 capitalize">{f.replace(/_/g, ' ')}</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form[f]}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                />
              </div>
            ))}
            <div className="flex justify-between gap-2 pt-2 flex-wrap">
              {editId ? (
                <button
                  type="button"
                  disabled={deletingId === editId || saving}
                  onClick={() => excluirTipo({ id: editId, nome: form.nome, codigo: form.codigo })}
                  className="px-3 py-2 text-red-700 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                >
                  {deletingId === editId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Excluir tipo
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={() => setModal(false)} className="px-3 py-2 border rounded-lg">
                  <X className="h-4 w-4 inline" />
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={save}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposEquipamentosPage;
